import React, { useState } from 'react';
import type { Track, AudioFile } from '../services/trackTypes';
import { Link } from 'react-router-dom';
import { MusicalNoteIcon, PauseIcon, PlayIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import CassetteLoader from './CassetteLoader';
import { SketchProgressBar } from './sketchy';
import { useFloating, useInteractions, useRole, useDismiss, offset, flip, shift } from '@floating-ui/react';

interface DragBindings {
  dragHandleProps?: any;
}

export interface TrackTileKanbanProps extends DragBindings {
  track: Track;
  isAccessible: boolean;
  isEditable: boolean;
  t: (key: string, defaultValue?: any) => string;
  // Artwork
  artworkUrl?: string | null;
  artworkLoading?: boolean;
  onArtworkClick?: () => void;
  // Delete
  deleting?: boolean;
  uncollaborating?: boolean;
  onDelete?: () => void;
  // Audio preview
  previewAudio?: AudioFile | undefined;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  // Sharing
  sharedRole?: 'viewer' | 'editor' | null;
  isOwner?: boolean;
}

const Tooltip: React.FC<{ content: string; children: React.ReactElement }> = ({ content, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    middleware: [offset(8), flip(), shift()],
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const { getFloatingProps } = useInteractions([
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ]);

  return (
    <>
      {React.cloneElement(children, { ref: refs.setReference, onMouseEnter: () => setIsOpen(true), onMouseLeave: () => setIsOpen(false) })}
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg"
        >
          {content}
        </div>
      )}
    </>
  );
};

const TrackTileKanban: React.FC<TrackTileKanbanProps> = ({
  track,
  isAccessible,
  isEditable,
  t,
  artworkUrl,
  artworkLoading,
  onArtworkClick,
  deleting,
  uncollaborating,
  onDelete,
  previewAudio,
  isPlaying,
  onTogglePlay,
  dragHandleProps,
  sharedRole,
  isOwner = true,
}) => {
  return (
    <div
      className={`group relative bg-[var(--color-surface)] rounded-lg shadow-sm hover:shadow-xl transition-all duration-300 border border-[var(--color-border)] overflow-hidden h-full flex flex-col ${!isAccessible ? 'opacity-60 pointer-events-none' : ''} ${deleting || uncollaborating ? 'opacity-50 pointer-events-none' : ''} ${!isEditable && isOwner ? 'opacity-60 grayscale-[0.6]' : ''}`}
      data-testid={`track-tile-${track.id}`}
    >
      
      {/* Premium Required Badge for Owned Tracks */}
      {!isEditable && isOwner && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium z-10 border border-yellow-200">
          <LockClosedIcon className="w-3 h-3" />
          {t('dashboard.premiumRequired')}
        </div>
      )}

      {/* Delete/Uncollaborate Loading Overlay */}
      {(deleting || uncollaborating) && (
        <div className="absolute inset-0 flex items-center justify-center z-20 rounded-lg" style={{ backgroundColor: 'var(--overlay-bg)' }}>
          <div className="flex flex-col items-center gap-3">
            <CassetteLoader size={48} variant="inline" />
            <span className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>
              {deleting ? t('dashboard.deletingTrack') : t('dashboard.leaving')}
            </span>
          </div>
        </div>
      )}

      <div className="p-3 flex-1 flex flex-col" {...dragHandleProps}>
        {/* Top row: Artwork and buttons */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex-shrink-0">
              {artworkUrl ? (
                <img
                  src={artworkUrl || undefined}
                  alt={t('track.artwork')}
                  className="w-full h-full object-cover rounded-lg shadow-lg border border-gray-200 cursor-pointer"
                  onClick={onArtworkClick}
                />
              ) : (
                <div className="relative w-full h-full">
                  {artworkLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center z-20 rounded-lg" style={{ backgroundColor: 'var(--overlay-bg)' }}>
                      <CassetteLoader size={48} variant="inline" />
                    </div>
                  ) : null}
                  <div className="w-full h-full gradient-brand-br rounded-lg flex items-center justify-center shadow-lg">
                    <MusicalNoteIcon className="h-4 w-4 text-white" />
                  </div>
                </div>
              )}
            </div>
            {/* Access Level Indicator - Next to artwork in flex layout */}
            {!isOwner && sharedRole && (
              <div className="flex-shrink-0">
                {sharedRole === 'viewer' ? (
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--collab-blue-bg)' }} title={t('share.canView')}>
                    <EyeIcon className="w-3 h-3" style={{ color: 'var(--collab-blue-text)' }} />
                  </div>
                ) : sharedRole === 'editor' ? (
                  <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center" title={t('share.canEdit')}>
                    <PencilIcon className="w-3 h-3 text-amber-600" />
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {previewAudio && (
              <button
                className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-[var(--color-hover)] transition flex-shrink-0 text-gray-500"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePlay && onTogglePlay(); }}
                aria-label={isPlaying ? t('track.stop') : t('track.play')}
              >
                {isPlaying ? <PauseIcon className="w-3 h-3" /> : <PlayIcon className="w-3 h-3" />}
              </button>
            )}
            {onDelete && (
              <button
                className="p-1 rounded-full transition hover:bg-red-100 text-red-500 hover:text-red-700"
                title={t('dashboard.removeTrack')}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
        {/* Middle row: Track name */}
        <div className="mb-2">
          <Tooltip content={track.name}>
            <Link to={`/tracks/${track.id}`} className="hover:underline focus:outline-none" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-[var(--color-text)] group-hover:text-[var(--brand-from)] transition-colors truncate">
                {track.name}
              </h3>
            </Link>
          </Tooltip>
        </div>
        {/* Progress bar */}
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
            <span>{t('track.progress')}</span>
            <span>{track.progress}%</span>
          </div>
          <SketchProgressBar value={track.progress} className="mt-1" data-testid={`track-tile-progress-${track.id}`} />
        </div>
      </div>
    </div>
  );
};

export default TrackTileKanban;


